// Angora Mosaics storefront core: dialogs, basket, announcement bar, scroll rows, chat and search loading.
const AM = (window.AM = window.AM || {});
const CHAT = AM.chat || {};
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Money, announcements and toasts */
let moneyFormatter = null;
try {
  moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || "en", { style: "currency", currency: AM.currency || "GBP" });
} catch {
  // An unknown currency code: fall back to plain numbers below.
}
AM.formatMoney = (cents) => {
  const value = Number(cents) / 100;
  return moneyFormatter ? moneyFormatter.format(value) : `${value.toFixed(2)} ${AM.currency || ""}`.trim();
};

AM.announce = (message) => {
  // While a modal dialog is open everything outside it is inert, so use the live region inside it.
  const region = document.querySelector("dialog[open] > [data-am-live]") || document.querySelector("body > [data-am-live]");
  if (!region) return;
  region.textContent = "";
  window.setTimeout(() => { region.textContent = message; }, 60);
};

// Visible feedback for buttons that have no message area of their own (quick add, basket steppers).
let toastTimer;
AM.toast = (message) => {
  const host = document.querySelector("dialog.am-drawer[open]") || document.body;
  let toast = host.querySelector(":scope > .am-toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.className = "am-toast";
    toast.setAttribute("role", "alert");
    host.append(toast);
  }
  toast.hidden = false;
  toast.textContent = message;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { toast.hidden = true; }, 6000);
};

/* Dialogs */
const openers = new WeakMap();
const closing = new WeakMap();

function cancelClose(dialog) {
  const pending = closing.get(dialog);
  if (!pending) return false;
  window.clearTimeout(pending.timer);
  dialog.removeEventListener("animationend", pending.onEnd);
  closing.delete(dialog);
  dialog.classList.remove("is-closing");
  return true;
}

AM.openDialog = (id, opener) => {
  const dialog = document.getElementById(id);
  if (!dialog) return dialog;
  // Reopened while the close animation runs: keep it open.
  if (cancelClose(dialog) || dialog.open) return dialog;
  openers.set(dialog, opener || document.activeElement);
  dialog.showModal();
  document.documentElement.classList.add("am-lock");
  dialog.dispatchEvent(new CustomEvent("am:open", { bubbles: true }));
  return dialog;
};

AM.closeDialog = (target) => {
  const dialog = typeof target === "string" ? document.getElementById(target) : target;
  if (!dialog || !dialog.open || closing.has(dialog)) return;
  const finish = () => {
    cancelClose(dialog);
    if (dialog.open) dialog.close();
  };
  if (reducedMotion()) return finish();
  const onEnd = (event) => {
    if (event.target === dialog) finish();
  };
  dialog.classList.add("is-closing");
  dialog.addEventListener("animationend", onEnd);
  closing.set(dialog, { onEnd, timer: window.setTimeout(finish, 320) });
};

function wireDialog(dialog) {
  if (dialog.dataset.amWired) return;
  dialog.dataset.amWired = "true";
  const live = document.createElement("div");
  live.className = "am-live";
  live.setAttribute("aria-live", "polite");
  live.dataset.amLive = "";
  dialog.append(live);
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    AM.closeDialog(dialog);
  });
  dialog.addEventListener("close", () => {
    if (!document.querySelector("dialog.am-drawer[open]")) document.documentElement.classList.remove("am-lock");
    const opener = openers.get(dialog);
    if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
  });
  dialog.addEventListener("click", (event) => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    const outside = event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
    if (outside) AM.closeDialog(dialog);
  });
}
$$("dialog.am-drawer").forEach(wireDialog);

document.addEventListener("click", (event) => {
  const opener = event.target.closest("[data-am-open]");
  if (opener) {
    const dialog = document.getElementById(opener.dataset.amOpen);
    if (!dialog) return;
    event.preventDefault();
    wireDialog(dialog);
    AM.openDialog(dialog.id, opener);
    return;
  }
  const closer = event.target.closest("[data-am-close]");
  if (closer) {
    event.preventDefault();
    AM.closeDialog(closer.closest("dialog"));
  }
});

/* Basket */
const CART_SECTION = "am-cart-drawer";
const PAGE_SECTION = "am-cart-page";
const cartSections = () => (document.querySelector("[data-am-cart-page]") ? `${CART_SECTION},${PAGE_SECTION}` : CART_SECTION);

async function postJSON(url, body) {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) {
    const error = new Error(data.description || data.message || "Something went wrong. Please try again.");
    error.status = response.status;
    throw error;
  }
  return data;
}

AM.cart = {
  async get() {
    const response = await fetch(`${AM.routes.cart}.js`, { headers: { Accept: "application/json" } });
    return response.json();
  },

  render(sections) {
    if (!sections) return;
    const targets = { [CART_SECTION]: "#am-cart [data-am-cart-inner]", [PAGE_SECTION]: "[data-am-cart-page] [data-am-cart-inner]" };
    for (const [name, selector] of Object.entries(targets)) {
      const html = sections[name];
      if (!html) continue;
      const fresh = new DOMParser().parseFromString(html, "text/html").querySelector("[data-am-cart-inner]");
      const current = document.querySelector(selector);
      if (!fresh || !current) continue;
      // Grow the pair-offer bar from its old length instead of jumping.
      const oldBar = current.querySelector(".am-cart__offer-bar span");
      const newBar = fresh.querySelector(".am-cart__offer-bar span");
      const target = newBar?.style.transform;
      if (oldBar && newBar && !reducedMotion()) newBar.style.transform = oldBar.style.transform;
      current.replaceWith(fresh);
      if (oldBar && newBar && !reducedMotion()) {
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => { newBar.style.transform = target; }));
      }
    }
  },

  updateCount(cart) {
    $$("[data-am-cart-count]").forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    $$('[data-am-open="am-cart"]').forEach((el) => {
      el.setAttribute("aria-label", `Basket, ${cart.item_count} ${cart.item_count === 1 ? "item" : "items"}`);
    });
  },

  emit(cart, source) {
    document.dispatchEvent(new CustomEvent("am:cart", { detail: { cart, source } }));
  },

  async add(items, { source = "form" } = {}) {
    const data = await postJSON(`${AM.routes.cartAdd}.js`, {
      items,
      sections: cartSections(),
      sections_url: window.location.pathname,
    });
    this.render(data.sections);
    const cart = await this.get();
    this.updateCount(cart);
    this.emit(cart, source);
    return cart;
  },

  async change(key, quantity) {
    const cart = await postJSON(`${AM.routes.cartChange}.js`, {
      id: key,
      quantity,
      sections: cartSections(),
      sections_url: window.location.pathname,
    });
    this.render(cart.sections);
    this.updateCount(cart);
    this.emit(cart, "change");
    return cart;
  },

  async note(text) {
    return postJSON(`${AM.routes.cartUpdate}.js`, { note: text });
  },

  // Re-read the basket after a failed change (Shopify can apply part of a change, e.g. capped by stock).
  async refresh() {
    const response = await fetch(`${window.location.pathname}?sections=${cartSections()}`);
    if (response.ok) this.render(await response.json());
    const cart = await this.get();
    this.updateCount(cart);
    this.emit(cart, "refresh");
    return cart;
  },
};

// The basket is re-rendered after every change, so focus has to be put back by hand.
const basketScope = (el) => (el.closest("[data-am-cart-page]") ? "[data-am-cart-page]" : "#am-cart");
function focusInBasket(scope, ...selectors) {
  const root = document.querySelector(scope);
  const target = selectors.map((selector) => root?.querySelector(selector)).find(Boolean)
    || root?.querySelector("#am-cart-title, #am-cart-page-title");
  target?.focus({ preventScroll: true });
}

const savedButtons = new WeakMap();
function setBusy(button, busy, busyText) {
  if (!button) return;
  if (busy) {
    savedButtons.set(button, [...button.childNodes].map((node) => node.cloneNode(true)));
    button.setAttribute("aria-busy", "true");
    button.disabled = true;
    const label = button.querySelector("[data-am-buy-label]");
    if (busyText && label) label.textContent = busyText;
    else if (busyText && !button.querySelector("svg")) button.textContent = busyText;
  } else {
    button.removeAttribute("aria-busy");
    button.disabled = false;
    if (savedButtons.has(button)) button.replaceChildren(...savedButtons.get(button));
  }
}

/* Any form with data-am-add posts to the basket without leaving the page (plain form post without JS). */
document.addEventListener("submit", async (event) => {
  const form = event.target.closest("form[data-am-add]");
  if (!form) return;
  event.preventDefault();
  const button = event.submitter || form.querySelector('[type="submit"]');
  const errorBox = form.querySelector("[data-am-error]");
  if (errorBox) errorBox.textContent = "";
  const data = new FormData(form);
  const items = [];
  const id = Number(data.get("id"));
  if (id) items.push({ id, quantity: Math.max(1, Number(data.get("quantity") || 1)) });
  form.querySelectorAll("[data-am-addon]:checked").forEach((input) => items.push({ id: Number(input.value), quantity: 1 }));
  if (!items.length) return;
  const inBasket = form.closest("#am-cart, [data-am-cart-page]");
  const scope = inBasket ? basketScope(form) : null;
  setBusy(button, true, form.dataset.amBusyText || "Adding…");
  try {
    await AM.cart.add(items, { source: form.dataset.amAdd || "form" });
    if (inBasket) focusInBasket(scope);
    else AM.openDialog("am-cart", button);
    AM.announce(items.length > 1 ? `${items.length} lamps added to your basket` : "Added to your basket");
  } catch (error) {
    if (errorBox) errorBox.textContent = error.message;
    else AM.toast(error.message);
  } finally {
    if (button?.isConnected) setBusy(button, false);
  }
});

// After a removal, offer to put the lamp back where it was.
function offerUndo(scope, removed) {
  const root = document.querySelector(scope);
  if (!root) return;
  const list = root.querySelector(".am-cart__lines");
  const row = document.createElement(list ? "li" : "div");
  row.className = "am-cart__undo";
  const text = document.createElement("span");
  text.textContent = `Removed ${removed.title}.`;
  const button = document.createElement("button");
  button.type = "button";
  button.className = "am-link";
  button.textContent = "Undo";
  button.addEventListener("click", async () => {
    setBusy(button, true, "Restoring…");
    try {
      await AM.cart.add([{ id: removed.id, quantity: removed.quantity }], { source: "undo" });
      focusInBasket(scope);
      AM.announce(`${removed.title} is back in your basket`);
    } catch (error) {
      setBusy(button, false);
      AM.toast(error.message);
    }
  });
  row.append(text, button);
  if (list) list.insertBefore(row, list.children[removed.index] || null);
  else root.querySelector(".am-cart__empty")?.prepend(row);
  button.focus({ preventScroll: true });
}

/* Basket quantity and remove buttons (delegated, because the basket is re-rendered). */
document.addEventListener("click", async (event) => {
  const control = event.target.closest("[data-am-qty]");
  if (!control) return;
  const line = control.closest("[data-am-line]");
  if (!line) return;
  event.preventDefault();
  const scope = basketScope(line);
  const key = line.dataset.amLine;
  const variant = line.dataset.amVariant;
  const action = control.dataset.amQtyAction;
  const quantity = Number(control.dataset.amQty);
  const removed = quantity === 0
    ? { id: Number(line.dataset.amVariant), quantity: Number(line.dataset.amQuantity) || 1, title: line.dataset.amTitle || "Lamp", index: [...line.parentElement.children].indexOf(line) }
    : null;
  line.classList.add("is-updating");
  try {
    await AM.cart.change(key, quantity);
    if (removed) {
      offerUndo(scope, removed);
      AM.announce(`Removed ${removed.title} from your basket`);
    } else {
      // Line keys change when a discount starts or stops applying, so fall back to the variant.
      focusInBasket(
        scope,
        `[data-am-line="${CSS.escape(key)}"] [data-am-qty-action="${action}"]`,
        `[data-am-variant="${CSS.escape(variant)}"] [data-am-qty-action="${action}"]`,
      );
      AM.announce("Basket updated");
    }
  } catch (error) {
    line.classList.remove("is-updating");
    AM.toast(error.message);
    await AM.cart.refresh().catch(() => {});
    focusInBasket(scope, `[data-am-variant="${CSS.escape(variant)}"] [data-am-qty-action="${action}"]`);
  }
});

// Gift note: saved as the shopper types, and also sent with the checkout form (name="note").
let noteTimer;
document.addEventListener("input", (event) => {
  const note = event.target.closest("[data-am-cart-note]");
  if (!note) return;
  const error = note.closest(".am-field")?.querySelector("[data-am-note-error]");
  window.clearTimeout(noteTimer);
  noteTimer = window.setTimeout(() => {
    AM.cart.note(note.value)
      .then(() => { if (error) error.textContent = ""; })
      .catch(() => { if (error) error.textContent = "Your note did not save just now. It will still go with your order when you check out."; });
  }, 500);
});

/* Announcement bar: one message at a time, with previous and next buttons. Auto-rotation pauses on hover or focus and stops once a shopper uses the buttons. */
class AmAnnouncement extends HTMLElement {
  connectedCallback() {
    this.items = $$(".am-announcement__msg", this);
    if (this.items.length < 2) return;
    this.index = 0;
    this.addEventListener("click", (event) => {
      const step = event.target.closest("[data-am-step]");
      if (!step) return;
      this.stop();
      this.show(this.index + Number(step.dataset.amStep));
    });
    if (reducedMotion()) return;
    this.paused = false;
    const pause = () => { this.paused = true; };
    const resume = () => { this.paused = false; };
    this.addEventListener("mouseenter", pause);
    this.addEventListener("mouseleave", resume);
    this.addEventListener("focusin", pause);
    this.addEventListener("focusout", resume);
    this.timer = window.setInterval(() => {
      if (!this.paused && !document.hidden) this.show(this.index + 1);
    }, 6000);
  }
  show(index) {
    const count = this.items.length;
    this.items[this.index].classList.add("is-hidden");
    this.index = (index + count) % count;
    this.items[this.index].classList.remove("is-hidden");
  }
  stop() {
    window.clearInterval(this.timer);
    this.timer = null;
  }
  disconnectedCallback() {
    this.stop();
  }
}
if (!customElements.get("am-announcement")) customElements.define("am-announcement", AmAnnouncement);

/* Scroll rows: previous and next buttons for mouse and trackpad users (touch users swipe). */
function initScroller(scroller, index) {
  const template = document.getElementById("am-tpl-scroller-nav");
  if (!template || scroller.dataset.amNav) return;
  scroller.dataset.amNav = "true";
  scroller.id ||= `am-scroller-${index}`;
  const nav = template.content.firstElementChild.cloneNode(true);
  const [prev, next] = nav.querySelectorAll("button");
  prev.setAttribute("aria-controls", scroller.id);
  next.setAttribute("aria-controls", scroller.id);
  // The buttons sit in the section header's action slot (am-section-header), or in a legacy row head.
  const root = scroller.closest("section, .am-section, .shopify-section");
  const sectionHead = root?.querySelector(".am-sh");
  let head = sectionHead ? sectionHead.querySelector(".am-sh__actions") : root?.querySelector(".am-row__head, .am-quotes__head");
  if (sectionHead && !head) {
    head = document.createElement("div");
    head.className = "am-sh__actions";
    sectionHead.append(head);
  }
  nav.hidden = true; // shown by the first measurement if the row overflows
  if (head) {
    head.append(nav);
  } else {
    nav.classList.add("am-scroller-nav--solo");
    scroller.before(nav);
  }
  const update = () => {
    const max = scroller.scrollWidth - scroller.clientWidth;
    nav.hidden = max < 8;
    prev.disabled = scroller.scrollLeft < 8;
    next.disabled = scroller.scrollLeft > max - 8;
  };
  const step = (direction) => scroller.scrollBy({ left: direction * scroller.clientWidth * 0.9, behavior: reducedMotion() ? "auto" : "smooth" });
  prev.addEventListener("click", () => step(-1));
  next.addEventListener("click", () => step(1));
  let frame = 0;
  const schedule = () => {
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(update);
  };
  scroller.addEventListener("scroll", schedule, { passive: true });
  // The first ResizeObserver callback does the initial measurement, after layout, without forcing one.
  new ResizeObserver(update).observe(scroller);
  new MutationObserver(schedule).observe(scroller, { childList: true });
}
const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
if (finePointer) $$(".am-scroller").forEach(initScroller);

/* Reveal on scroll: .am-reveal fades up when it enters the viewport (immediate under reduced motion).
   Later additions (Ajax results, rows) are picked up automatically; AM.reveal(root) re-scans by hand. */
const revealer = "IntersectionObserver" in window && !reducedMotion()
  ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add("is-in");
        revealer.unobserve(entry.target);
      }
    }, { rootMargin: "0px 0px -6% 0px", threshold: 0.04 })
  : null;
AM.reveal = (root = document) => {
  const items = root.matches?.(".am-reveal") ? [root, ...$$(".am-reveal", root)] : $$(".am-reveal", root);
  items.forEach((el) => {
    if (el.classList.contains("is-in")) return;
    if (revealer) revealer.observe(el);
    else el.classList.add("is-in");
  });
};
// Items already on screen are marked before the hidden state switches on (one batched read, then the writes),
// so nothing visible blinks and nothing stays hidden if this script never runs.
{
  const viewportHeight = window.innerHeight;
  const onScreen = $$(".am-reveal").filter((el) => {
    const box = el.getBoundingClientRect();
    return box.bottom > 0 && box.top < viewportHeight;
  });
  onScreen.forEach((el) => el.classList.add("is-in"));
  document.documentElement.classList.add("am-reveal-ready");
}
AM.reveal();
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) if (node.nodeType === 1) AM.reveal(node);
  }
}).observe(document.body, { childList: true, subtree: true });
// Reached only when everything above ran: switches off the 4s CSS safety net for .am-reveal (see am-base.css).
document.documentElement.classList.add("am-reveal-live");

/* Header: compact once the page scrolls past the sentinel at 120px. An IntersectionObserver, so no scroll listener. */
const sentinel = document.querySelector(".am-scroll-sentinel");
const header = document.querySelector(".am-header");
if (sentinel && header && "IntersectionObserver" in window) {
  new IntersectionObserver(([entry]) => {
    const scrolled = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    document.documentElement.classList.toggle("am-scrolled", scrolled);
    header.classList.toggle("is-compact", scrolled);
    if (scrolled) closeMega();
  }).observe(sentinel);
}

/* Mega menu (desktop nav): opens on hover intent or click, closes on Escape, an outside click, or focus leaving it. */
const megaButton = document.querySelector("[data-am-mega]");
const mega = megaButton ? document.getElementById(megaButton.getAttribute("aria-controls")) : null;
let megaTimer = 0;
let megaByHover = false;
function setMega(open) {
  if (!mega || mega.hidden === !open) return;
  mega.hidden = !open;
  megaButton.setAttribute("aria-expanded", String(open));
}
function closeMega() {
  window.clearTimeout(megaTimer);
  megaByHover = false;
  setMega(false);
}
if (mega) {
  const item = megaButton.closest("li") || megaButton.parentElement;
  megaButton.addEventListener("click", () => {
    if (mega.hidden) {
      megaByHover = false;
      setMega(true);
    } else if (megaByHover) {
      megaByHover = false; // the first click after a hover-open keeps it open
    } else {
      closeMega();
    }
  });
  if (finePointer) {
    item.addEventListener("mouseenter", () => {
      window.clearTimeout(megaTimer);
      megaTimer = window.setTimeout(() => { if (mega.hidden) { megaByHover = true; setMega(true); } }, 80);
    });
    item.addEventListener("mouseleave", () => {
      window.clearTimeout(megaTimer);
      megaTimer = window.setTimeout(closeMega, 220);
    });
  }
  item.addEventListener("focusout", (event) => {
    if (!item.contains(event.relatedTarget)) closeMega();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || mega.hidden) return;
    closeMega();
    megaButton.focus();
  });
  document.addEventListener("click", (event) => {
    if (!mega.hidden && !item.contains(event.target)) closeMega();
  });
}

/* Chat: the Chatwoot SDK loads only when a shopper asks for it. */
let chatLoading;
function loadChat() {
  if (chatLoading) return chatLoading;
  chatLoading = new Promise((resolve, reject) => {
    window.chatwootSettings = { position: "right", type: "standard", hideMessageBubble: true };
    const script = document.createElement("script");
    script.src = `${CHAT.url}/packs/js/sdk.js`;
    script.async = true;
    script.onload = () => {
      window.addEventListener("chatwoot:ready", () => resolve(), { once: true });
      window.chatwootSDK.run({ websiteToken: CHAT.token, baseUrl: CHAT.url });
      window.setTimeout(resolve, 5000);
    };
    script.onerror = reject;
    document.head.append(script);
  });
  return chatLoading;
}

document.addEventListener("click", async (event) => {
  const trigger = event.target.closest("[data-am-chat]");
  if (!trigger) return;
  event.preventDefault();
  if (!CHAT.enabled || !CHAT.url || !CHAT.token) {
    window.location.href = "/pages/contact";
    return;
  }
  trigger.setAttribute("aria-busy", "true");
  try {
    await loadChat();
    const openDialog = trigger.closest("dialog");
    if (openDialog) AM.closeDialog(openDialog);
    window.$chatwoot?.toggle("open");
  } catch {
    window.location.href = "/pages/contact";
  } finally {
    trigger.removeAttribute("aria-busy");
  }
});

/* Search: the predictive search module loads the first time the dialog opens. */
document.addEventListener("am:open", (event) => {
  if (event.target.id !== "am-search") return;
  import(AM.assets.search).then((mod) => mod.init(event.target)).catch(() => {});
});

/* Read more: long text blocks start collapsed (description, collection text, SEO copy).
   Measured in a ResizeObserver callback, which runs after the browser's own layout, so start-up never forces one. */
let readMoreCount = 0;
function collapse(box, toggle, limit) {
  readMoreCount += 1;
  box.id = box.id || `am-readmore-${readMoreCount}`;
  box.style.setProperty("--am-readmore", `${limit}px`);
  box.classList.add("is-collapsed");
  toggle.hidden = false;
  toggle.setAttribute("aria-controls", box.id);
  toggle.setAttribute("aria-expanded", "false");
  toggle.textContent = "Read more";
  toggle.addEventListener("click", () => {
    const open = !box.classList.toggle("is-collapsed");
    toggle.setAttribute("aria-expanded", String(open));
    toggle.textContent = open ? "Show less" : "Read more";
  });
}
if ("ResizeObserver" in window) {
  const readMore = new ResizeObserver((entries) => {
    // Read every box first, then collapse, so one change never forces a re-measure of the next.
    const tall = entries
      .map(({ target: box }) => {
        readMore.unobserve(box);
        const toggle = box.parentElement.querySelector("[data-am-readmore-toggle]");
        const limit = Number(box.dataset.amReadmore) || 480;
        return toggle && box.scrollHeight >= limit + 120 ? { box, toggle, limit } : null;
      })
      .filter(Boolean);
    tall.forEach(({ box, toggle, limit }) => collapse(box, toggle, limit));
  });
  $$("[data-am-readmore]").forEach((box) => readMore.observe(box));
}

/* Glow: a faint halo in a photo's own colour behind the product gallery, "Light it up" and "See them glow".
   The theme setting "Soft glow behind lamp photos" (settings.am_glow) decides whether the [data-am-glow] layers are
   rendered at all; with none on the page nothing below does any work. Each layer names a 16px same-origin copy of its
   photo in data-glow-src. During idle time the copy is drawn on a 16x16 canvas and its dominant vivid colour is picked
   (hue bins, weighted by saturation and brightness); the result is cached per URL (memory and sessionStorage) and
   painted as --am-glow on the layer, which gets .is-lit. Dark or near-black (relative luminance under 0.08) and grey
   (saturation under 0.25) photos get no halo. Static: no canvas per frame, no animation loops. */
AM.glow = (() => {
  const colours = new Map();
  const idle = window.requestIdleCallback ? (fn) => window.requestIdleCallback(fn, { timeout: 2000 }) : (fn) => window.setTimeout(fn, 80);
  const key = (url) => `am:glow:${url}`;
  const store = {
    get(url) { try { return window.sessionStorage.getItem(key(url)); } catch { return null; } },
    set(url, value) { try { window.sessionStorage.setItem(key(url), value); } catch { /* best effort */ } },
  };
  const channel = (v) => { const c = v / 255; return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const luminance = (r, g, b) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  // Dominant vivid colour: every pixel that is neither dark nor grey votes for its hue bin (12 of 30 degrees), weighted by
  // saturation and brightness. The winning bin must hold at least 4% of the pixels, and vivid pixels at least 8% in all.
  const pick = (data) => {
    const bins = Array.from({ length: 12 }, () => ({ w: 0, n: 0, r: 0, g: 0, b: 0 }));
    let vivid = 0;
    const total = data.length / 4;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      const sat = max ? (max - min) / max : 0;
      const lum = luminance(r, g, b);
      if (sat < 0.25 || lum < 0.08) continue;
      vivid += 1;
      const d = max - min;
      let h = 0;
      if (max === r) h = ((g - b) / d) % 6;
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h = (h * 60 + 360) % 360;
      const bin = bins[Math.floor(h / 30) % 12];
      const w = sat * sat * Math.sqrt(lum);
      bin.w += w; bin.n += 1; bin.r += r * w; bin.g += g * w; bin.b += b * w;
    }
    if (vivid < total * 0.08) return "";
    const best = bins.reduce((a, c) => (c.w > a.w ? c : a), bins[0]);
    if (!best.w || best.n < total * 0.04) return "";
    const r = best.r / best.w, g = best.g / best.w, b = best.b / best.w;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    if (luminance(r, g, b) < 0.08 || (max - min) / max < 0.25) return "";
    // A clean tint for the halo: the photo's hue, lifted to a mid lightness and a sure saturation.
    const l = (max + min) / 510;
    const d = (max - min) / 255;
    let sat = d / (1 - Math.abs(2 * l - 1));
    let h = 0;
    if (max === r) h = ((g - b) / (max - min)) % 6;
    else if (max === g) h = (b - r) / (max - min) + 2;
    else h = (r - g) / (max - min) + 4;
    h = Math.round((h * 60 + 360) % 360);
    sat = Math.round(Math.min(0.9, Math.max(0.55, sat)) * 100);
    return `hsl(${h} ${sat}% ${Math.round(Math.min(0.62, Math.max(0.5, l)) * 100)}%)`;
  };
  // The CDN URL on the shop's own origin, so the canvas stays readable.
  const sameOrigin = (url) => {
    try { const u = new URL(url, window.location.href); return `${window.location.origin}${u.pathname}${u.search}`; } catch { return url; }
  };
  const read = (url) => new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, 16, 16);
        resolve(pick(ctx.getImageData(0, 0, 16, 16).data));
      } catch { resolve(""); }
    };
    img.onerror = () => resolve("");
    img.src = sameOrigin(url);
  });
  const colour = (url) => {
    if (!url) return Promise.resolve("");
    if (!colours.has(url)) {
      const stored = store.get(url);
      colours.set(url, stored !== null ? Promise.resolve(stored) : read(url).then((c) => { store.set(url, c); return c; }));
    }
    return colours.get(url);
  };
  const paint = (layer, url) => {
    if (!layer) return;
    const src = url || layer.dataset.glowSrc;
    layer.dataset.glowSrc = src || "";
    layer.dataset.glowDone = "";
    colour(src).then((c) => {
      if (layer.dataset.glowSrc !== src) return; // the photo changed meanwhile
      layer.style.setProperty("--am-glow", c || "transparent");
      layer.classList.toggle("is-lit", Boolean(c));
    });
  };
  const init = () => {
    const layers = $$("[data-am-glow][data-glow-src]:not([data-glow-done])");
    if (!layers.length) return;
    idle(() => layers.forEach((layer) => paint(layer)));
  };
  return { colour, paint, init };
})();
AM.glow.init();
